import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { waitForFirebaseAuth } from '../services/firebaseAuthBridge';
import { getTierLimits } from '../utils/tierLimits';

export interface SubscriptionStatus {
  isPremium: boolean;
  isFreePremium?: boolean;
  subscriptionStatus?: string;
  subscriptionPeriodEnd?: Date;
  currentPeriodEnd?: Date;
  cancelAt?: Date;
  loading: boolean;
  error: string | null;
  userRole?: string | null;
  freeFormRemaining?: number;
  // NEW: Tier system fields
  subscriptionTier?: 'free' | 'pro' | 'proPlus' | 'enterprise';
  formLimit?: number | null;
  submissionLimit?: number | null;
  exportLimit?: number | null;
  recipientLimit?: number | null;
  storageLimit?: number | null;
  trialEndsAt?: Date;
  legacyPricing?: boolean;
  pricingLocked?: boolean;
}

const DEV_EMAIL_DOMAINS = ['yourdomain.com'];
// Only system-level roles bypass subscription — Managers must have an active subscription
const PREMIUM_EXEMPT_ROLES = ['Admin', 'Owner'];

export const useSubscription = () => {
  const { user } = useUser();
  const [status, setStatus] = useState<SubscriptionStatus>({
    isPremium: false,
    loading: true,
    error: null,
    userRole: null
  });

  const checkSubscription = async () => {
    if (!user?.id) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }));

      // Wait for the Clerk->Firebase bridge sign-in before this self-read, else
      // it races the sign-in and is denied under the rules — which silently
      // defaulted the user to free/User (missing role, subscription, stats).
      await waitForFirebaseAuth();
      const userRef = doc(db, "users", user.id);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setStatus({
          isPremium: false,
          loading: false,
          error: "User not found",
          userRole: null
        });
        return;
      }

      const userData = userSnap.data();
      const userRole = userData.role || null;
      
      const hasRoleBasedPremium = userRole && PREMIUM_EXEMPT_ROLES.includes(userRole);
      const hasFreePremium = userData.freePremium === true;
      const urlParams = new URLSearchParams(window.location.search);
      const testPremium = urlParams.get('testPremium') === 'true';
      const userEmail = user.primaryEmailAddress?.emailAddress || '';
      const isDeveloper = DEV_EMAIL_DOMAINS.some(domain => userEmail.endsWith(`@${domain}`));
      
      // Add a check for isTestSubscription
      const isTestSubscription = userData.isTestSubscription === true;

      // Modified condition to include isTestSubscription
      if ((isDeveloper || testPremium || isTestSubscription) && process.env.NODE_ENV !== 'production') {
        console.log('Developer or test subscription mode: Granting premium access');

        if (!userData.isPremium) {
          await updateDoc(userRef, {
            isPremium: true,
            proUser: true,
            subscriptionStatus: 'active',
            isTestSubscription: true,
            premiumSince: new Date()
          });
        }

        setStatus({
          isPremium: true,
          subscriptionStatus: 'active (test mode)',
          subscriptionTier: 'enterprise',
          loading: false,
          error: null,
          userRole
        });
        return;
      }

      // Check for isTestSubscription when not in dev mode
      if (isTestSubscription) {
        console.log('Test subscription detected: Granting premium access');

        setStatus({
          isPremium: true,
          subscriptionStatus: 'active (test subscription)',
          subscriptionTier: 'enterprise',
          loading: false,
          error: null,
          userRole
        });
        return;
      }

      if (hasRoleBasedPremium || hasFreePremium) {
        if (!userData.isPremium) {
          await updateDoc(userRef, {
            isPremium: true,
            proUser: true,
            ...(hasFreePremium ? { freePremium: true } : {}),
            premiumSince: userData.premiumSince || new Date()
          });
        }

        // Use enterprise tier so gated features (edit submissions, landing page, etc.) match premium access
        const t = userData.subscriptionTier;
        const roleOrFreeTier: 'free' | 'pro' | 'proPlus' | 'enterprise' =
          (t === 'pro' || t === 'proPlus' || t === 'enterprise') ? t : 'enterprise';

        setStatus({
          isPremium: true,
          isFreePremium: hasFreePremium,
          subscriptionStatus: hasRoleBasedPremium ? 'role-based access' : 'free premium',
          subscriptionTier: roleOrFreeTier,
          loading: false,
          error: null,
          userRole
        });
        return;
      }

      // Flags are PRIMARY source of truth (backward compatible)
      // Note: proUser and freePremium are optional fields (may not exist)
      const hasFlags = !!(userData.isPremium || userData.proUser === true || userData.freePremium === true || userData.isTestSubscription);
      const hasTier = userData.subscriptionTier && userData.subscriptionTier !== 'free';
      
      // Determine tier: flags first, then tier field
      let tier: 'free' | 'pro' | 'proPlus' | 'enterprise' = 'free';
      if (hasFlags) {
        tier = 'enterprise';  // Flags indicate premium = Enterprise
        // Sync tier if it doesn't match (async, don't block)
        if (userData.subscriptionTier !== 'enterprise') {
          updateDoc(userRef, { subscriptionTier: 'enterprise' }).catch(err => 
            console.error('Error syncing tier:', err)
          );
        }
      } else if (hasTier) {
        tier = userData.subscriptionTier;
      }
      
      // isPremium check (PRESERVE existing behavior for backward compatibility)
      const isPremium = hasFlags || tier !== 'free';

      if (isPremium && userData.subscriptionPeriodEnd && !hasRoleBasedPremium && !hasFreePremium && !isTestSubscription) {
        const periodEnd = userData.subscriptionPeriodEnd.toDate?.() || new Date(userData.subscriptionPeriodEnd);
        const now = new Date();
        const gracePeriodDays = 7; // 7-day grace period (matches Stripe retry window)
        const gracePeriodEnd = new Date(periodEnd);
        gracePeriodEnd.setDate(gracePeriodEnd.getDate() + gracePeriodDays);
        
        // Check if subscription has expired (after grace period)
        if (now > gracePeriodEnd) {
          // Grace period has ended - revoke access
          await updateDoc(userRef, {
            isPremium: false,
            proUser: false,
            subscriptionStatus: 'expired',
            subscriptionTier: 'free'
          });

          setStatus({
            isPremium: false,
            subscriptionStatus: 'expired',
            subscriptionPeriodEnd: periodEnd,
            subscriptionTier: 'free',
            loading: false,
            error: null,
            userRole
          });
          return;
        } else if (now > periodEnd && now <= gracePeriodEnd) {
          // In grace period - maintain access but mark as in grace period
          setStatus({
            isPremium: true, // Still premium during grace period
            subscriptionStatus: 'grace_period',
            subscriptionPeriodEnd: periodEnd,
            subscriptionTier: tier, // Keep current tier during grace period
            loading: false,
            error: null,
            userRole
          });
          // Don't return - continue to set other status fields below
        }
        // If subscription hasn't expired yet, continue normally
      }

      // Changed this line to default to 1 for new users who don't have the property set
      // This ensures new users get one free form as intended
      const freeFormRemaining = typeof userData.freeFormRemaining === 'number' ? userData.freeFormRemaining : 1;

      // Get limits from Firebase config (admin can change these)
      // If user already has limits set, use those (they were set by webhook)
      // Otherwise, load from config based on tier
      let tierLimits;
      try {
        tierLimits = await getTierLimits(tier);
      } catch (error) {
        console.error('Error loading tier limits from config, using defaults:', error);
        // Fallback to defaults
        if (tier === 'pro') {
          tierLimits = { formLimit: 4, submissionLimit: 1000, exportLimit: 10, recipientLimit: 5, storageLimit: 100 };
        } else if (tier === 'enterprise') {
          tierLimits = { formLimit: null, submissionLimit: null, exportLimit: null, recipientLimit: null, storageLimit: 1000 };
        } else {
          tierLimits = { formLimit: 1, submissionLimit: null, exportLimit: null, recipientLimit: 1, storageLimit: 10 };
        }
      }

      setStatus({
        isPremium,
        isFreePremium: hasFreePremium,
        freeFormRemaining,
        subscriptionStatus: userData.subscriptionStatus,
        subscriptionPeriodEnd: userData.subscriptionPeriodEnd?.toDate?.() || 
          (userData.subscriptionPeriodEnd ? new Date(userData.subscriptionPeriodEnd) : undefined),
        cancelAt: userData.subscriptionCancelledAt?.toDate?.() || 
          (userData.subscriptionCancelledAt ? new Date(userData.subscriptionCancelledAt) : undefined),
        // NEW: Tier system fields
        subscriptionTier: tier,
        // Use limits from user document if set (by webhook), otherwise use config
        formLimit: userData.formLimit !== undefined ? userData.formLimit : tierLimits.formLimit,
        submissionLimit: userData.submissionLimit !== undefined ? userData.submissionLimit : tierLimits.submissionLimit,
        exportLimit: userData.exportLimit !== undefined ? userData.exportLimit : tierLimits.exportLimit,
        recipientLimit: userData.recipientLimit !== undefined ? userData.recipientLimit : tierLimits.recipientLimit,
        storageLimit: userData.storageLimit !== undefined ? userData.storageLimit : tierLimits.storageLimit,
        trialEndsAt: userData.trialEndsAt?.toDate?.() || (userData.trialEndsAt ? new Date(userData.trialEndsAt) : undefined),
        legacyPricing: userData.legacyPricing === true,
        pricingLocked: userData.pricingLocked === true,
        loading: false,
        error: null,
        userRole
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
      setStatus({
        isPremium: false,
        loading: false,
        error: "Failed to check subscription status",
        userRole: null
      });
    }
  };

  useEffect(() => {
    checkSubscription();
    const interval = setInterval(checkSubscription, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const refreshStatus = () => {
    checkSubscription();
  };

  const setTestPremium = async () => {
    if (!user?.id || process.env.NODE_ENV === 'production') return;
    
    try {
      const userRef = doc(db, "users", user.id);
      await updateDoc(userRef, {
        isPremium: true,
        proUser: true,
        subscriptionStatus: 'active',
        isTestSubscription: true,
        premiumSince: new Date()
      });
      checkSubscription();
    } catch (error) {
      console.error("Error setting test premium status:", error);
    }
  };

  const setFreePremium = async (userId: string, enableFree: boolean) => {
    try {
      const userRef = doc(db, "users", userId);
      
      if (enableFree) {
        // When enabling free premium, set Enterprise tier with unlimited limits
        await updateDoc(userRef, {
          isPremium: true,
          proUser: true,
          freePremium: true,
          subscriptionTier: 'enterprise',  // NEW: Sync tier
          formLimit: null,  // NEW: Unlimited
          submissionLimit: null,
          exportLimit: null,
          recipientLimit: null,
          storageLimit: 1000,
          premiumSince: new Date()
        });
      } else {
        // When disabling, check if user has Stripe subscription
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        if (userData?.stripeSubscriptionId && userData?.subscriptionStatus === 'active') {
          // User has active Stripe subscription - keep premium based on subscription
          // Don't change flags, webhook will handle it
          await updateDoc(userRef, {
            freePremium: false
            // Keep isPremium and proUser as-is (webhook will update based on subscription)
          });
        } else {
          // No Stripe subscription - set to Free tier
          await updateDoc(userRef, {
            isPremium: false,
            proUser: false,
            freePremium: false,
            subscriptionTier: 'free',  // NEW: Set to free tier
            formLimit: 1,
            submissionLimit: null,
            exportLimit: null,
            recipientLimit: 1,
            storageLimit: 10
          });
        }
      }

      if (userId === user?.id) {
        checkSubscription();
      }

      return true;
    } catch (error) {
      console.error("Error setting free premium status:", error);
      return false;
    }
  };

  const consumeFreeForm = async () => {
    if (!user?.id || status.isPremium || !status.freeFormRemaining || status.freeFormRemaining < 1) {
      return false;
    }

    try {
      const userRef = doc(db, "users", user.id);
      await updateDoc(userRef, {
        freeFormRemaining: Math.max(0, (status.freeFormRemaining - 1))
      });

      await checkSubscription();
      return true;
    } catch (error) {
      console.error("Error using free form:", error);
      return false;
    }
  };

  const canCreateForm = () => {
    return status.isPremium || (status.freeFormRemaining && status.freeFormRemaining > 0);
  };

  return {
    ...status,
    refreshStatus,
    setTestPremium,
    setFreePremium,
    consumeFreeForm,
    canCreateForm
  };
};

export default useSubscription;
